import React, {useState, useEffect, useCallback, useRef} from 'react'
import {Form, Spinner} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {
  FaUpload,
  FaChevronDown,
  FaTimes,
  FaFileAlt,
  FaStethoscope,
  FaHospital,
  FaBed,
  FaMoneyBillWave,
  FaPrescription,
  FaNotesMedical,
  FaPencilAlt,
  FaFileExcel,
} from 'react-icons/fa'
import styled, {keyframes, css} from 'styled-components'
import './../style.css'
import axios from 'axios'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import {useNavigate} from 'react-router-dom'
import {debounce} from 'lodash'
import {ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import CustomCalendar from '../../../../_metronic/layout/calendar'

// Type definitions
type StyledButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  $primary?: boolean
}

type LoadingSkeletonProps = React.HTMLAttributes<HTMLDivElement> & {
  $height?: string
}

type ProgressBarProps = React.HTMLAttributes<HTMLDivElement> & {
  $progress: number
}

type ValidationMessageProps = React.HTMLAttributes<HTMLDivElement> & {
  $isError?: boolean
}

type UploadAreaProps = React.HTMLAttributes<HTMLDivElement> & {
  $disabled?: boolean
}

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`

const StyledCard = styled.div`
  width: 100%;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 20px;
  animation: ${fadeIn} 0.3s ease;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`

const StyledSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  border-radius: 8px;
  background: #f8f9fa;
  transition: all 0.3s ease;

  &:hover {
    background: #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`

const StyledLabel = styled.label`
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #059bb6;
  }
`

const StyledInput = styled(Form.Control)`
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  transition: all 0.3s ease;

  &:focus {
    border-color: #059bb6;
    box-shadow: 0 0 0 3px rgba(5, 155, 182, 0.1);
  }

  &:hover {
    border-color: ${(props) => (props.readOnly ? '#e9ecef' : '#059bb6')};
    cursor: ${(props) => (props.readOnly ? 'not-allowed' : 'text')};
  }

  &[readOnly] {
    background-color: #f5f5f5;
    color: #6c757d;
  }
`

const StyledButton = styled.button<StyledButtonProps>`
  ${(props) => css`
    background: ${props.$primary ? '#059BB6' : '#F5F8FA'};
    color: ${props.$primary ? 'white' : '#059BB6'};
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(5, 155, 182, 0.2);
    }

    &:active {
      transform: translateY(0);
    }
  `}
`

const UploadArea = styled.div<UploadAreaProps>`
  ${(props) => css`
    border: 2px dashed ${props.$disabled ? '#ccc' : '#059BB6'};
    border-radius: 8px;
    padding: 24px;
    text-align: center;
    cursor: ${props.$disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.3s ease;
    opacity: ${props.$disabled ? 0.7 : 1};

    &:hover {
      background: ${props.$disabled ? 'none' : 'rgba(5, 155, 182, 0.05)'};
    }
  `}
`

const FileItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  margin: 8px 0;
  transition: all 0.3s ease;

  &:hover {
    background: #ffffff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`

const ProgressBar = styled.div<ProgressBarProps>`
  ${(props) => css`
    width: ${props.$progress}%;
    height: 8px;
    background: linear-gradient(90deg, #059bb6 0%, #05b6a6 100%);
    border-radius: 4px;
    transition: width 0.3s ease;
  `}
`

const StyledTextArea = styled(Form.Control)`
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    border-color: #059bb6;
    box-shadow: 0 0 0 3px rgba(5, 155, 182, 0.1);
  }

  &:hover {
    border-color: ${(props) => (props.readOnly ? '#e9ecef' : '#059bb6')};
    cursor: ${(props) => (props.readOnly ? 'not-allowed' : 'text')};
  }

  &[readOnly] {
    background-color: #f5f5f5;
    color: #6c757d;
  }
`

const CharacterCount = styled.small`
  color: #6c757d;
  font-size: 0.875em;
  margin-top: 4px;
  display: block;
  text-align: right;
  transition: color 0.3s ease;

  &.warning {
    color: #ffc107;
  }

  &.danger {
    color: #dc3545;
  }
`

const IcdDropdownItem = styled.div`
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`

const IcdName = styled.div`
  font-weight: 500;
  color: #2c3e50;
`

const IcdCode = styled.div`
  font-size: 0.875em;
  color: #6c757d;
`

const LoadingSkeleton = styled.div<LoadingSkeletonProps>`
  ${(props) => css`
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: loading 1.5s infinite;
    border-radius: 4px;
    height: ${props.$height || '24px'};
    margin-bottom: 8px;

    @keyframes loading {
      0% {
        background-position: 200% 0;
      }
      100% {
        background-position: -200% 0;
      }
    }
  `}
`

const Tooltip = styled.div`
  position: relative;
  display: inline-block;

  &:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
`

const TooltipText = styled.div`
  visibility: hidden;
  opacity: 0;
  background-color: #2c3e50;
  color: white;
  text-align: center;
  padding: 8px 12px;
  border-radius: 4px;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  transition: opacity 0.3s ease;
  white-space: nowrap;
  font-size: 0.875em;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #2c3e50 transparent transparent transparent;
  }
`

const ValidationMessage = styled.div<ValidationMessageProps>`
  ${(props) => css`
    color: ${props.$isError ? '#dc3545' : '#28a745'};
    font-size: 0.875em;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
  `}
`

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
`

const ErrorInput = styled(StyledInput)`
  &.error {
    border-color: #dc3545;
    animation: ${shake} 0.5s ease;
  }
`

interface DiagnosisProps {
  claimDetails?: any
  isEditing: boolean
  onEditClick: () => void
  onValidateDiagnosis?: (diagnosisData: any) => void
  isFromDailyMonitoring?: boolean
  diagnosisData: any
  onDiagnosisDataChange: (data: any) => void
}

const Diagnosis: React.FC<DiagnosisProps> = ({
  claimDetails,
  isEditing,
  onEditClick,
  onValidateDiagnosis,
  isFromDailyMonitoring,
  diagnosisData,
  onDiagnosisDataChange,
}) => {
  const user = useAuth()
  const {auth} = useAuth()
  const navigate = useNavigate()
  const {
    mainDiagnosis,
    supportingDiagnosis,
    supportingDiagnosis2,
    supportingDiagnosis3,
    doctor,
    polyName,
    polyCode,
    remark,
    fax,
    deposit,
    originalIcdCodes,
    supportingDiagnosis4,
    rencanaTindakan,
    losEstimation,
    remarkRejectVoid,
    indate,
  } = diagnosisData
  console.log('remarkRejectVoid', remarkRejectVoid)
  console.log('remark', remark)
  console.log('diagnosisData', diagnosisData)
  console.log('claimDetails', claimDetails)
  const {formatMessage: f} = useIntl()
  const [icdData, setIcdData] = useState<any[]>([])
  const [icdData2, setIcdData2] = useState<any[]>([])
  const [icdData3, setIcdData3] = useState<any[]>([])
  const [icdData4, setIcdData4] = useState<any[]>([])
  const [openDropdown, setOpenDropdown] = useState<number | null>(null)
  const [files, setFiles] = useState<File[]>([])
  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [successMessage, setSuccessMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [documentsData, setDocumentsData] = useState<any[]>([])
  const [documentFiles, setDocumentFiles] = useState<Record<number, any[]>>([])
  const [ketentuanKhusus, setKetentuanKhusus] = useState('')
  const [catatan, setCatatan] = useState('')
  const jsonString = claimDetails?.claimDetails?.claim?.detailBenefit
  const parsedJson = jsonString ? JSON.parse(jsonString) : {}
  const status = claimDetails?.claimDetails?.claim?.status

  const jsonString2 = claimDetails?.claimDetails?.claim?.claimData
  const parsedJson2 = jsonString2 ? JSON.parse(jsonString2) : {}

  // Extract stable values for useCallback dependencies
  const planCode = parsedJson.PlanCode
  const benefitCode = parsedJson.BenefitCode

  // Extract stable values from parsedJson2 to prevent infinite loops
  const icdCode1 = parsedJson2.IcdCode1
  const icdCode2 = parsedJson2.IcdCode2
  const icdCode3 = parsedJson2.IcdCode3
  const icdCode4 = parsedJson2.IcdCode4
  const icdName1 = parsedJson2.IcdName1
  const icdName2 = parsedJson2.IcdName2
  const icdName3 = parsedJson2.IcdName3
  const icdName4 = parsedJson2.IcdName4
  const doctorFromData = parsedJson2.Doctor
  const polyNameFromData = parsedJson2.PolyName
  const polyCodeFromData = parsedJson2.PolyCode
  const remarkFromData = parsedJson2.Remark
  const depositFromData = parsedJson2.Deposit
  const treatmentPlanFromData = parsedJson2.TreatmentPlan
  const losFromData = parsedJson2.Los
  const indateFromData = parsedJson2.InDate

  const mainDiagnosisDropdownRef = useRef<HTMLDivElement>(null)
  const supportingDiagnosis1DropdownRef = useRef<HTMLDivElement>(null)
  const supportingDiagnosis2DropdownRef = useRef<HTMLDivElement>(null)
  const supportingDiagnosis3DropdownRef = useRef<HTMLDivElement>(null)
  const supportingDiagnosis4DropdownRef = useRef<HTMLDivElement>(null)
  const [isMainDiagnosisDropdownVisible, setIsMainDiagnosisDropdownVisible] = useState(false)
  const [isSupportingDiagnosis1DropdownVisible, setIsSupportingDiagnosis1DropdownVisible] =
    useState(false)
  const [isSupportingDiagnosis2DropdownVisible, setIsSupportingDiagnosis2DropdownVisible] =
    useState(false)
  const [isSupportingDiagnosis3DropdownVisible, setIsSupportingDiagnosis3DropdownVisible] =
    useState(false)
  const [isSupportingDiagnosis4DropdownVisible, setIsSupportingDiagnosis4DropdownVisible] =
    useState(false)

  const [deletingFiles, setDeletingFiles] = useState<Record<string, boolean>>({})
  const [selectedCurrency, setSelectedCurrency] = useState<string>('IDR')
  const parsedMemberDetail = claimDetails.claimDetails?.claim?.memberDetail
    ? JSON.parse(claimDetails.claimDetails?.claim?.memberDetail)
    : {}
  const [productCode, setProductCode] = useState<string>('')
  const [isProductCodeLoaded, setIsProductCodeLoaded] = useState<boolean>(false)
  console.log('Data Jaki :', parsedJson2)

  useEffect(() => {
    if (onValidateDiagnosis) {
      const isValid = Boolean(
        // mainDiagnosis && doctor && polyName && deposit && remark && rencanaTindakan
        mainDiagnosis && doctor && polyName && remark && rencanaTindakan
      )

      onValidateDiagnosis({
        mainDiagnosis,
        doctor,
        polyName,
        ketentuanKhusus,
        // deposit,
        remark,
        rencanaTindakan,
        isValid,
      })
    }
  }, [
    mainDiagnosis,
    doctor,
    polyName,
    ketentuanKhusus,
    // deposit,
    remark,
    rencanaTindakan,
    // onValidateDiagnosis
  ])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mainDiagnosisDropdownRef.current &&
        !mainDiagnosisDropdownRef.current.contains(event.target as Node)
      ) {
        setIsMainDiagnosisDropdownVisible(false)
      }
      if (
        supportingDiagnosis1DropdownRef.current &&
        !supportingDiagnosis1DropdownRef.current.contains(event.target as Node)
      ) {
        setIsSupportingDiagnosis1DropdownVisible(false)
      }
      if (
        supportingDiagnosis2DropdownRef.current &&
        !supportingDiagnosis2DropdownRef.current.contains(event.target as Node)
      ) {
        setIsSupportingDiagnosis2DropdownVisible(false)
      }
      if (
        supportingDiagnosis3DropdownRef.current &&
        !supportingDiagnosis3DropdownRef.current.contains(event.target as Node)
      ) {
        setIsSupportingDiagnosis3DropdownVisible(false)
      }
      if (
        supportingDiagnosis4DropdownRef.current &&
        !supportingDiagnosis4DropdownRef.current.contains(event.target as Node)
      ) {
        setIsSupportingDiagnosis4DropdownVisible(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const loadExistingData = async () => {
      // Skip if unsaved draft exists in sessionStorage (page refresh recovery)
      const claimNo = claimDetails?.claimDetails?.claim?.claimNumber
      if (claimNo && sessionStorage.getItem(`diagnosisDraft:${claimNo}`)) {
        console.log('Skipping data load: unsaved draft exists in sessionStorage')
        return
      }

      // Only skip if parsedJson2 doesn't exist or we're currently editing and data is already loaded
      if (!parsedJson2) {
        console.log('Skipping data load: parsedJson2 not available')
        return
      }

      // Don't reload if we're editing and already have data
      if (isEditing && (mainDiagnosis || doctor || polyName || remark)) {
        console.log('Skipping data load: editing mode and data already exists')
        return
      }

      console.log('Loading existing data from parsedJson2:', parsedJson2)

      const allUpdates: {
        doctor: string
        remark: string
        deposit: string
        polyCode: string
        polyName: string
        originalIcdCodes: any
        mainDiagnosis?: string
        supportingDiagnosis?: string
        rencanaTindakan?: string
        losEstimation?: string
        supportingDiagnosis4?: string
        supportingDiagnosis3?: string
        supportingDiagnosis2?: string
        supportingDiagnosis1?: string
        indate?: string
      } = {
        doctor: parsedJson2.Doctor || '',
        remark: parsedJson2.Remark || '',
        // deposit: String(parsedJson2.Deposit || ''),
        deposit:
          parsedJson2.Deposit !== undefined && parsedJson2.Deposit !== null
            ? String(parsedJson2.Deposit)
            : '',
        polyName: parsedJson2.PolyName || '',
        polyCode: parsedJson2.PolyCode || '',
        originalIcdCodes: {...originalIcdCodes},
      }

      // Add indate if available
      if (parsedJson2.InDate) {
        allUpdates.indate = parsedJson2.InDate
      }

      // Load basic data first, then fetch ICD details if productCode is available
      if (parsedJson2.IcdCode1) {
        if (productCode && isProductCodeLoaded) {
          console.log('Fetching ICD1 details for:', parsedJson2.IcdCode1)
          const icd1Details = await fetchIcdDetails(parsedJson2.IcdCode1)
          if (icd1Details) {
            allUpdates.mainDiagnosis = icd1Details.displayValue
            allUpdates.originalIcdCodes.icd1 = icd1Details.code
          }
        } else {
          // Fallback: use code and name from parsedJson2 if available
          const icdName = parsedJson2.IcdName1 || ''
          allUpdates.mainDiagnosis = icdName
            ? `${icdName} (${parsedJson2.IcdCode1})`
            : parsedJson2.IcdCode1
          allUpdates.originalIcdCodes.icd1 = parsedJson2.IcdCode1
          console.log('Using fallback for ICD1:', allUpdates.mainDiagnosis)
        }
      }

      if (parsedJson2.IcdCode2) {
        if (productCode && isProductCodeLoaded) {
          const icd2Details = await fetchIcdDetails(parsedJson2.IcdCode2)
          if (icd2Details) {
            allUpdates.supportingDiagnosis = icd2Details.displayValue
            allUpdates.originalIcdCodes.icd2 = icd2Details.code
          }
        } else {
          const icdName = parsedJson2.IcdName2 || ''
          allUpdates.supportingDiagnosis = icdName
            ? `${icdName} (${parsedJson2.IcdCode2})`
            : parsedJson2.IcdCode2
          allUpdates.originalIcdCodes.icd2 = parsedJson2.IcdCode2
        }
      }

      if (parsedJson2.IcdCode3) {
        if (productCode && isProductCodeLoaded) {
          const icd3Details = await fetchIcdDetails(parsedJson2.IcdCode3)
          if (icd3Details) {
            allUpdates.supportingDiagnosis3 = icd3Details.displayValue
            allUpdates.originalIcdCodes.icd3 = icd3Details.code
          }
        } else {
          const icdName = parsedJson2.IcdName3 || ''
          allUpdates.supportingDiagnosis3 = icdName
            ? `${icdName} (${parsedJson2.IcdCode3})`
            : parsedJson2.IcdCode3
          allUpdates.originalIcdCodes.icd3 = parsedJson2.IcdCode3
        }
      }

      if (parsedJson2.IcdCode4) {
        if (productCode && isProductCodeLoaded) {
          const icd4Details = await fetchIcdDetails(parsedJson2.IcdCode4)
          if (icd4Details) {
            allUpdates.supportingDiagnosis4 = icd4Details.displayValue
            allUpdates.originalIcdCodes.icd4 = icd4Details.code
          }
        } else {
          const icdName = parsedJson2.IcdName4 || ''
          allUpdates.supportingDiagnosis4 = icdName
            ? `${icdName} (${parsedJson2.IcdCode4})`
            : parsedJson2.IcdCode4
          allUpdates.originalIcdCodes.icd4 = parsedJson2.IcdCode4
        }
      }

      if (parsedJson2.TreatmentPlan) {
        allUpdates.rencanaTindakan = parsedJson2.TreatmentPlan
      }

      if (parsedJson2.Los) {
        // Ensure losEstimation is always stored as a string
        allUpdates.losEstimation = String(parsedJson2.Los)
      }

      console.log('Updating diagnosis data with:', allUpdates)
      onDiagnosisDataChange(allUpdates)
    }

    loadExistingData()
  }, [
    icdCode1,
    icdCode2,
    icdCode3,
    icdCode4,
    icdName1,
    icdName2,
    icdName3,
    icdName4,
    doctorFromData,
    polyNameFromData,
    polyCodeFromData,
    remarkFromData,
    depositFromData,
    treatmentPlanFromData,
    losFromData,
    indateFromData,
    isProductCodeLoaded,
    productCode,
    isEditing,
    mainDiagnosis,
    doctor,
    polyName,
    remark,
  ])

  // Secondary effect to update ICD details when productCode becomes available
  useEffect(() => {
    const updateIcdDetails = async () => {
      if (
        (!icdCode1 && !icdCode2 && !icdCode3 && !icdCode4) ||
        !productCode ||
        !isProductCodeLoaded
      )
        return

      const updates: any = {}
      let hasUpdates = false

      // Check if we need to update ICD1 details
      if (icdCode1 && mainDiagnosis === icdCode1) {
        const icd1Details = await fetchIcdDetails(icdCode1)
        if (icd1Details && icd1Details.displayValue !== mainDiagnosis) {
          updates.mainDiagnosis = icd1Details.displayValue
          hasUpdates = true
        }
      }

      // Check if we need to update ICD2 details
      if (icdCode2 && supportingDiagnosis === icdCode2) {
        const icd2Details = await fetchIcdDetails(icdCode2)
        if (icd2Details && icd2Details.displayValue !== supportingDiagnosis) {
          updates.supportingDiagnosis = icd2Details.displayValue
          hasUpdates = true
        }
      }

      // Check if we need to update ICD3 details
      if (icdCode3 && supportingDiagnosis3 === icdCode3) {
        const icd3Details = await fetchIcdDetails(icdCode3)
        if (icd3Details && icd3Details.displayValue !== supportingDiagnosis3) {
          updates.supportingDiagnosis3 = icd3Details.displayValue
          hasUpdates = true
        }
      }

      // Check if we need to update ICD4 details
      if (icdCode4 && supportingDiagnosis4 === icdCode4) {
        const icd4Details = await fetchIcdDetails(icdCode4)
        if (icd4Details && icd4Details.displayValue !== supportingDiagnosis4) {
          updates.supportingDiagnosis4 = icd4Details.displayValue
          hasUpdates = true
        }
      }

      if (hasUpdates) {
        onDiagnosisDataChange(updates)
      }
    }

    updateIcdDetails()
  }, [
    productCode,
    isProductCodeLoaded,
    icdCode1,
    icdCode2,
    icdCode3,
    icdCode4,
    mainDiagnosis,
    supportingDiagnosis,
    supportingDiagnosis3,
    supportingDiagnosis4,
  ])

  const defaultDocuments = [
    {label: 'Lembar Medis Awal', key: 0},
    {label: 'Copy KTP Pasien', key: 1},
    {label: 'Copy Kartu Asuransi Pasien', key: 2},
    {label: 'Surat Pernyataan (SP)', key: 3},
    {label: 'Medical Inquiry', key: 4},
    {label: 'Hasil Penunjang', key: 5},
  ]

  const validateFields = () => {
    if (!mainDiagnosis) {
      alert('Main ICD code is required.')
      return true
    }
    if (!doctor) {
      alert("Doctor's name is required.")
      return true
    }
    if (!polyName) {
      alert('Poly name is required.')
      return true
    }
    if (deposit) {
      // Convert formatted deposit to decimal format for validation
      const formattedDeposit = formatDepositForSubmission(deposit)
      if (isNaN(Number(formattedDeposit))) {
        alert('Deposit must be a valid number.')
        return true
      }
    }
    if (!remark) {
      alert('Remark is required.')
      return true
    }
    return false
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files)
      setFiles((prevFiles) => [...prevFiles, ...selectedFiles])
      console.log('Selected files:', selectedFiles)
      await uploadFiles(selectedFiles)
    }
  }

  const handleRemoveFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index))
  }

  const uploadFiles = async (filesToUpload: File[]) => {
    if (filesToUpload.length === 0) {
      console.log('No files to upload')
      return
    }

    // Check file size limit (10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB in bytes
    const oversizedFiles = filesToUpload.filter((file) => file.size > MAX_FILE_SIZE)

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((f) => f.name).join(', ')
      toast.error(`File size exceeds maximum allowed size of 10MB: ${fileNames}`, {
        position: 'bottom-right',
        autoClose: 5000,
      })
      return
    }

    const formData = new FormData()
    filesToUpload.forEach((file) => {
      formData.append('files', file, file.name)
    })

    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/UploadFiles`

    const queryParams = new URLSearchParams({
      claimNumber: claimNumber,
      typeDocument: 'Prescription',
      user: user.currentUser?.email || '',
      status: 'VALID',
      id: '0',
    })

    const fullUrl = `${apiUrl}?${queryParams.toString()}`

    setIsUploading(true)
    setUploadProgress(0)
    setSuccessMessage('')

    try {
      console.log('Uploading files with FormData:', formData)
      Array.from(formData.entries()).forEach((pair) => {
        console.log(pair[0], pair[1])
      })

      const response = await axios.post(fullUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          accept: '*/*',
        },
        onUploadProgress: (progressEvent) => {
          const total = progressEvent.total || 0
          const current = progressEvent.loaded
          const percentage = Math.round((current / total) * 100)
          setUploadProgress(percentage)
        },
      })

      if (response.data.isSuccess) {
        console.log('Files uploaded successfully:', response.data)

        const listFilesResponse = await axios.get(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}`,
          {
            headers: {
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
          }
        )

        if (listFilesResponse.data.isSuccess) {
          const prescriptionDocs = listFilesResponse.data.data.find(
            (doc: any) => doc.label === 'Prescription'
          )
          setDocumentsData((prev) => {
            const newData = prev.filter((item) => item.label !== 'Prescription')
            return [...newData, prescriptionDocs].filter(Boolean)
          })
        }

        setSuccessMessage('Files uploaded successfully!')
        setTimeout(() => {
          setSuccessMessage('')
        }, 3000)
      } else {
        console.error('Failed to upload files:', response)
        toast.error('Failed to upload files')
      }
    } catch (error: any) {
      console.error('Error uploading files:', error)
      console.error('Error response:', error.response?.data)
      toast.error(error.response?.data?.message || 'Error uploading files')
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const fetchIcdData = useCallback(
    debounce(async (identifier: string, searchTerm: string) => {
      if (searchTerm.length <= 2 || !productCode) return

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_USER_URL}/api/Exclusion/GetPaginatedMstIcd10s?searchTerm=${searchTerm}&pageNumber=1&pageSize=10&productCode=${productCode}&planCode=${planCode}&benefitCode=${benefitCode}`,
          {
            headers: {
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
          }
        )

        const data = response.data.data || []
        console.log('Fetched ICD data:', data) // Debug log

        if (identifier === 'main') {
          setIcdData(data)
          setIsMainDiagnosisDropdownVisible(true)
        }
      } catch (err) {
        console.error('Error fetching ICD-10 data:', err)
        setIcdData([])
        setIsMainDiagnosisDropdownVisible(false)
      }
    }, 300),
    [productCode, auth?.token, user.currentUser?.token, planCode, benefitCode]
  )

  const fetchIcdData2 = useCallback(
    debounce(async (identifier: string, searchTerm: string) => {
      if (searchTerm.length <= 2 || !productCode) return

      const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Exclusion/GetPaginatedMstIcd10s?searchTerm=${searchTerm}&pageNumber=1&pageSize=10&productCode=${productCode}&planCode=${planCode}&benefitCode=${benefitCode}`
      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }
      try {
        const response = await axios.get(apiUrl, {headers})
        const data = response.data.data || []
        console.log(`Fetched data for ${identifier}:`, data)
        setIcdData2(data)
        setIsSupportingDiagnosis1DropdownVisible(data.length > 0)
      } catch (err) {
        console.error('Error fetching ICD-10 data:', err)
      }
    }, 300),
    [productCode, auth?.token, user.currentUser?.token, planCode, benefitCode]
  )

  const fetchIcdData3 = useCallback(
    debounce(async (identifier: string, searchTerm: string) => {
      if (searchTerm.length <= 2 || !productCode) return

      const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Exclusion/GetPaginatedMstIcd10s?searchTerm=${searchTerm}&pageNumber=1&pageSize=10&productCode=${productCode}&planCode=${planCode}&benefitCode=${benefitCode}`
      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }
      try {
        const response = await axios.get(apiUrl, {headers})
        const data = response.data.data || []
        console.log(`Fetched data for ${identifier}:`, data)
        setIcdData3(data)
        setIsSupportingDiagnosis2DropdownVisible(data.length > 0)
      } catch (err) {
        console.error('Error fetching ICD-10 data:', err)
      }
    }, 300),
    [productCode, auth?.token, user.currentUser?.token, planCode, benefitCode]
  )

  const fetchIcdData4 = useCallback(
    debounce(async (identifier: string, searchTerm: string) => {
      if (searchTerm.length <= 2 || !productCode) return

      const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Exclusion/GetPaginatedMstIcd10s?searchTerm=${searchTerm}&pageNumber=1&pageSize=10&productCode=${productCode}&planCode=${planCode}&benefitCode=${benefitCode}`
      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }
      try {
        const response = await axios.get(apiUrl, {headers})
        const data = response.data.data || []
        console.log(`Fetched data for ${identifier}:`, data)
        setIcdData4(data)
        setIsSupportingDiagnosis3DropdownVisible(data.length > 0)
      } catch (err) {
        console.error('Error fetching ICD-10 data:', err)
      }
    }, 300),
    [productCode, auth?.token, user.currentUser?.token, planCode, benefitCode]
  )

  const fetchIcdName = useCallback(
    debounce(async (searchTerm: string) => {
      if (searchTerm.length <= 2) return

      const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Exclusion/GetPaginatedMstIcd10s?searchTerm=${searchTerm}&pageNumber=1&pageSize=10&productCode=${productCode}&planCode=${planCode}&benefitCode=${benefitCode}`
      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }
      try {
        const response = await axios.get(apiUrl, {headers})
        const data = response.data.data || []
        console.log(`Fetched data for ${searchTerm}:`, data)
        setIcdData(data.map((item: any) => ({code: item.code, name: item.name})))
      } catch (err) {
        console.error('Error fetching ICD-10 data:', err)
      }
    }, 300),
    [productCode, auth?.token, user.currentUser?.token, planCode, benefitCode]
  )

  const setMainDiagnosis = (value: string) => {
    onDiagnosisDataChange({mainDiagnosis: value})
  }

  const handleSupportingDiagnosisChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    console.log('Input for ICD 10 - 2:', value)
    onDiagnosisDataChange({supportingDiagnosis: value})
    if (value.length > 2) {
      fetchIcdData2('supporting2', value)
    } else {
      setIcdData2([])
      setIsSupportingDiagnosis1DropdownVisible(false)
    }
  }

  const handleSupportingDiagnosisChange3 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    console.log('Input for ICD 10 - 3:', value)
    onDiagnosisDataChange({supportingDiagnosis3: value})
    if (value.length > 2) {
      fetchIcdData3('supporting3', value)
    } else {
      setIcdData3([])
      setIsSupportingDiagnosis2DropdownVisible(false)
    }
  }

  const handleSupportingDiagnosisChange4 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    console.log('Input for ICD 10 - 4:', value)
    onDiagnosisDataChange({supportingDiagnosis4: value})
    if (value.length > 2) {
      fetchIcdData4('supporting4', value)
    } else {
      setIcdData4([])
      setIsSupportingDiagnosis3DropdownVisible(false)
    }
  }

  const handleSelectIcd = (icd: {code: string; name: string}) => {
    onDiagnosisDataChange({
      mainDiagnosis: `${icd.name} (${icd.code})`,
      icd1: icd.code,
    })
    setIcdData([])
  }

  const handleSelectSupportingIcd = (icd: {code: string; name: string}) => {
    onDiagnosisDataChange({
      supportingDiagnosis: `${icd.name} (${icd.code})`,
      icd2: icd.code,
    })
    setIcdData2([])
  }

  const handleSelectSupportingIcd3 = (icd: {code: string; name: string}) => {
    onDiagnosisDataChange({
      supportingDiagnosis3: `${icd.name} (${icd.code})`,
      icd3: icd.code,
    })
    setIcdData3([])
  }

  const handleSelectSupportingIcd4 = (icd: {code: string; name: string}) => {
    onDiagnosisDataChange({
      supportingDiagnosis4: `${icd.name} (${icd.code})`,
      icd4: icd.code,
    })
    setIcdData4([])
  }

  const toggleDropdown = (index: number) => {
    setOpenDropdown(openDropdown === index ? null : index)
  }

  const fetchDocuments = async () => {
    const claimNumber = claimDetails?.claimDetails?.claim?.claimNumber
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }

    try {
      const response = await axios.get(apiUrl, {headers})
      if (response.data.isSuccess) {
        setDocumentsData(response.data.data)

        const updatedFiles: Record<number, any[]> = {}
        defaultDocuments.forEach((doc) => {
          const matchingData = response.data.data.find((d: any) => d.label === doc.label)
          updatedFiles[doc.key] = matchingData
            ? matchingData.document.map((file: any) => ({
                name: file.name,
                id: file.id,
                type: file.name.split('.').pop(),
              }))
            : []
        })
        setDocumentFiles(updatedFiles)
      } else {
        console.error('Failed to fetch documents:', response.data.message)
      }
    } catch (error) {
      console.error('Error fetching documents:', error)
    }
  }

  useEffect(() => {
    fetchDocuments()
  }, [claimDetails])

  const formatDepositForSubmission = (depositValue: string) => {
    if (!depositValue) return ''

    // Remove thousands separators and convert comma to dot
    return depositValue.replace(/\./g, '').replace(',', '.')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (validateFields()) return

    setLoading(true)

    // Get the ICD codes from either original codes or extract from display values
    const getIcdCode = (displayValue: string | undefined) => {
      if (!displayValue) return ''
      const match = displayValue.match(/\(([^)]+)\)/)
      return match ? match[1] : displayValue
    }

    const finalData = {
      claimNo: claimDetails?.claimDetails?.claim?.claimNumber,
      icdCode1: originalIcdCodes?.icd1 || getIcdCode(mainDiagnosis || ''),
      icdCode2: originalIcdCodes?.icd2 || getIcdCode(supportingDiagnosis || ''),
      icdCode3: originalIcdCodes?.icd3 || getIcdCode(supportingDiagnosis3 || ''),
      icdCode4: originalIcdCodes?.icd4 || getIcdCode(supportingDiagnosis4 || ''),
      doctor: doctor || '',
      polyCode: polyName || '',
      polyName: polyName || '',
      remark: remark || '',
      deposit: deposit ? formatDepositForSubmission(deposit) : '',
      currency: selectedCurrency,
      treatmentPlan: rencanaTindakan || '',
      los: losEstimation ? parseInt(losEstimation) : 0,
      updatedBy: user.currentUser?.email,
      BenefitDetail: {
        id: parsedJson?.Id,
        benefitName: parsedJson?.BenefitName,
        planName: parsedJson?.PlanName,
        benefitCode: parsedJson?.BenefitCode,
        condition: parsedJson?.Condition,
        annualLimit: parsedJson?.AnnualLimit,
        subBenefits:
          parsedJson?.SubBenefits?.map((subBenefit: any) => ({
            id: subBenefit.SubBenefitId,
            category: 'INPATIENT',
            SubBenefitName: subBenefit.SubBenefitName,
            isCashless: true,
            isReimburstement: false,
            annualLimit: subBenefit.Amount,
            type: 'INPATIENT',
            claimValue: subBenefit.Amount,
            details: subBenefit.Details,
            condition: subBenefit?.Condition || [],
          })) || [],
      },
    }

    console.log('Final Data to Submit: ', JSON.stringify(finalData, null, 2))

    try {
      const headers = {
        Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
      }

      if (process.env.REACT_APP_API_PROCESS_CLAIM_URL !== undefined) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/ProcessClaim/INPATIENT`,
          finalData,
          {headers}
        )

        if (response.data.isSuccess) {
          toast.success('Successfully saved as draft!', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: 'light',
            onOpen: () => {
              console.log('Toast opened')
            },
            onClose: () => {
              onEditClick()
            },
          })
        } else {
          toast.error(response.data.message || 'Failed to save draft')
        }
      } else {
        toast.error('API URL is undefined')
      }
    } catch (error: any) {
      console.error('Error:', error)
      toast.error(error.response?.data?.message || 'Failed to save draft')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    await handleSubmit(e)
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault()
    onEditClick()
  }

  const handleSaveClick = () => {
    onEditClick()
  }

  const handleCancelClick = (e: React.MouseEvent) => {
    e.preventDefault()

    // Reset form to original data from parsedJson2
    onDiagnosisDataChange({
      mainDiagnosis: parsedJson2.IcdCode1
        ? `${parsedJson2.IcdName1} (${parsedJson2.IcdCode1})`
        : '',
      supportingDiagnosis: parsedJson2.IcdCode2
        ? `${parsedJson2.IcdName2} (${parsedJson2.IcdCode2})`
        : '',
      supportingDiagnosis3: parsedJson2.IcdCode3
        ? `${parsedJson2.IcdName3} (${parsedJson2.IcdCode3})`
        : '',
      supportingDiagnosis4: parsedJson2.IcdCode4
        ? `${parsedJson2.IcdName4} (${parsedJson2.IcdCode4})`
        : '',
      doctor: parsedJson2.Doctor || '',
      polyName: parsedJson2.PolyName || '',
      deposit: parsedJson2.Deposit || '',
      remark: parsedJson2.Remark || '',
      rencanaTindakan: parsedJson2.TreatmentPlan || '',
      losEstimation: parsedJson2.Los || '',
      originalIcdCodes: {
        icd1: parsedJson2.IcdCode1 || '',
        icd2: parsedJson2.IcdCode2 || '',
        icd3: parsedJson2.IcdCode3 || '',
        icd4: parsedJson2.IcdCode4 || '',
      },
    })

    // Close all dropdowns
    setIsMainDiagnosisDropdownVisible(false)
    setIsSupportingDiagnosis1DropdownVisible(false)
    setIsSupportingDiagnosis2DropdownVisible(false)
    setIsSupportingDiagnosis3DropdownVisible(false)

    // Clear any ICD data
    setIcdData([])
    setIcdData2([])
    setIcdData3([])
    setIcdData4([])

    // Switch back to view mode
    onEditClick()
  }

  const renderPrescriptionDocuments = () => {
    const prescriptionDocs =
      documentsData.find((doc) => doc.label === 'Prescription')?.document || []
    return prescriptionDocs.map((doc) => (
      <div
        key={doc.id}
        className='file-item'
        style={{
          display: 'flex',
          alignItems: 'center',
          margin: '8px',
          justifyContent: 'space-between',
          backgroundColor: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          width: 'calc(100% - 16px)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
          border: '1px solid #f0f0f0',
          transition: 'all 0.2s ease',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.08)'
          e.currentTarget.style.transform = 'translateY(-2px)'
          e.currentTarget.style.borderColor = '#e0e0e0'
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.04)'
          e.currentTarget.style.transform = 'translateY(0)'
          e.currentTarget.style.borderColor = '#f0f0f0'
        }}
      >
        <div style={{display: 'flex', alignItems: 'center', overflow: 'hidden'}}>
          <div
            style={{
              backgroundColor: '#e9f5f8',
              padding: '10px',
              borderRadius: '8px',
              marginRight: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FaFileAlt style={{color: '#059BB6'}} />
          </div>
          <div style={{overflow: 'hidden'}}>
            <div
              style={{
                fontWeight: 500,
                color: '#2c3e50',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '200px',
              }}
            >
              {doc.name}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: '#6c757d',
                marginTop: '2px',
              }}
            >
              {new Date().toLocaleDateString()} • {(doc.size || '').toString()}
            </div>
          </div>
        </div>
        {isEditing && (
          <button
            onClick={() => handleDeleteFile(doc)}
            disabled={deletingFiles[doc.id]}
            style={{
              border: 'none',
              background: deletingFiles[doc.id] ? '#f8d7da' : '#fff4f4',
              padding: '8px',
              borderRadius: '6px',
              cursor: 'pointer',
              color: '#dc3545',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s ease',
            }}
            title='Delete file'
            onMouseOver={(e) => {
              if (!deletingFiles[doc.id]) {
                e.currentTarget.style.background = '#f8d7da'
              }
            }}
            onMouseOut={(e) => {
              if (!deletingFiles[doc.id]) {
                e.currentTarget.style.background = '#fff4f4'
              }
            }}
          >
            {deletingFiles[doc.id] ? (
              <span style={{fontSize: '12px', fontWeight: 500}}>
                {f({id: 'CLAIM.INPATIENT.DELETING_FILE'})}
              </span>
            ) : (
              <FaTimes />
            )}
          </button>
        )}
      </div>
    ))
  }

  console.log('IS EDITING :', isEditing)

  const fetchIcdDetails = async (icdCode: string) => {
    if (!icdCode || !productCode) return null

    const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Exclusion/GetPaginatedMstIcd10s?searchTerm=${icdCode}&pageNumber=1&pageSize=10&productCode=${productCode}&planCode=${planCode}&benefitCode=${benefitCode}`
    const headers = {
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }
    try {
      const response = await axios.get(apiUrl, {headers})
      console.log('ICD Response:', response.data) // Debug log

      // Find exact match by code
      const matchingIcd = response.data.data.find((icd: any) => icd.code === icdCode)
      if (matchingIcd) {
        return {
          displayValue: `${matchingIcd.name} (${matchingIcd.code})`,
          code: matchingIcd.code,
          name: matchingIcd.name,
        }
      }
      return {displayValue: icdCode, code: icdCode, name: ''}
    } catch (err) {
      console.error('Error fetching ICD details:', err)
      return {displayValue: icdCode, code: icdCode, name: ''}
    }
  }

  const handleMainDiagnosisChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    onDiagnosisDataChange({mainDiagnosis: value})

    if (value.length > 2) {
      fetchIcdData('main', value)
    } else {
      setIcdData([])
      setIsMainDiagnosisDropdownVisible(false)
    }
  }

  const handleDeleteFile = async (file: any) => {
    const claimNumber = claimDetails.claimDetails.claim.claimNumber
    const apiUrl = `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/Provider/DeleteFile?id=${file.id}&claimNumber=${claimNumber}`

    try {
      setDeletingFiles((prev) => ({...prev, [file.id]: true}))

      const response = await axios.post(
        apiUrl,
        {},
        {
          headers: {
            Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
          },
        }
      )

      if (response) {
        // Refresh the documents list
        const listFilesResponse = await axios.get(
          `${process.env.REACT_APP_API_CLAIM_URL}/api/Claims/ListFiles/${claimNumber}`,
          {
            headers: {
              Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
            },
          }
        )

        if (listFilesResponse.data.isSuccess) {
          const prescriptionDocs = listFilesResponse.data.data.find(
            (doc: any) => doc.label === 'Prescription'
          )
          setDocumentsData((prev) => {
            const newData = prev.filter((item) => item.label !== 'Prescription')
            return [...newData, prescriptionDocs].filter(Boolean)
          })
        }

        toast.success('File deleted successfully!', {
          position: 'top-right',
          autoClose: 3000,
        })
      } else {
        toast.error('Failed to delete file', {
          position: 'top-right',
          autoClose: 3000,
        })
      }
    } catch (error) {
      console.error('Error deleting file:', error)
      toast.error('Error deleting file', {
        position: 'top-right',
        autoClose: 3000,
      })
    } finally {
      setDeletingFiles((prev) => {
        const newState = {...prev}
        delete newState[file.id]
        return newState
      })
    }
  }

  useEffect(() => {
    console.log('Current ICD Values:', {
      icd1: diagnosisData?.icd1,
      icd2: diagnosisData?.icd2,
      icd3: diagnosisData?.icd3,
      icd4: diagnosisData?.icd4,
      mainDiagnosis,
      supportingDiagnosis,
      supportingDiagnosis3,
      supportingDiagnosis4,
      originalIcdCodes,
      doctor,
      polyName,
      remark,
      deposit,
      rencanaTindakan,
      losEstimation,
    })
  }, [
    diagnosisData,
    mainDiagnosis,
    supportingDiagnosis,
    supportingDiagnosis3,
    supportingDiagnosis4,
    originalIcdCodes.icd1,
    originalIcdCodes.icd2,
    originalIcdCodes.icd3,
    originalIcdCodes.icd4,
    doctor,
    polyName,
    remark,
    deposit,
    rencanaTindakan,
    losEstimation,
  ])

  useEffect(() => {
    // Initialize currency from diagnosisData if it exists
    if (diagnosisData.currency) {
      setSelectedCurrency(diagnosisData.currency)
    }
  }, [diagnosisData.currency])

  const formatDisplayValue = (value) => {
    // if (value === 0 || value === '0') {
    //   return '0'
    // }

    if (!value) return ''

    // Handle if the value already has formatting
    if (typeof value === 'string' && value.includes('.')) {
      // If value is already formatted, return as is
      return value
    }

    // Convert value to string and replace dots with empty string to clean any existing format
    const valueStr = String(value).replace(/\./g, '')

    // If there's a comma, split and format the integer part
    if (valueStr.includes(',')) {
      const [integerPart, decimalPart] = valueStr.split(',')
      const formattedInteger = integerPart.replace(/\./g, '')
      const formattedValue = `${formattedInteger},${decimalPart}`
      return formattedValue
    }

    // Otherwise just format the integer
    const formattedValue = valueStr.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    return formattedValue

    // return value.toString()
  }

  const fetchMemberDetail = async () => {
    const apiUrl = `${process.env.REACT_APP_API_USER_URL}/api/Data/Provider/GetMemberByCard?ProviderCode=${user.currentUser?.providerCode}&CardNo=${parsedMemberDetail?.CardNumber}&Dob=${parsedMemberDetail?.DateOfBirth}`
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth?.token}` || `Bearer ${user.currentUser?.token}`,
    }
    try {
      setLoading(true)
      const response = await axios.get(apiUrl, {headers})
      if (response.data.isSuccess) {
        const productCodeData = response.data.data.productCode
        setProductCode(productCodeData)
        setIsProductCodeLoaded(true)
        console.log('Product code set:', productCodeData)
      }
    } catch (err) {
      console.error('Error fetching member detail:', err)
      setIsProductCodeLoaded(true) // Set to true even on error to prevent infinite loading
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMemberDetail()
  }, [])

  return (
    <>
      <ToastContainer
        position='top-right'
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
        style={{zIndex: 9999}}
      />

      <StyledCard>
        <form onSubmit={handleSave}>
          {loading ? (
            <div style={{padding: '20px'}}>
              <LoadingSkeleton $height='40px' />
              <LoadingSkeleton $height='80px' />
              <LoadingSkeleton $height='40px' />
            </div>
          ) : (
            <>
              {/* INPUT DATE */}
              <StyledSection>
                <CustomCalendar
                  label={
                    <Tooltip>
                      <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <FaBed style={{color: '#059bb6'}} />
                        {f({id: 'CLAIM.INPATIENT.ADMISSION_DATE'})}
                      </span>
                      <TooltipText className='tooltip-text'>
                        {f({id: 'CLAIM.INPATIENT.ADMISSION_DATE_TOOLTIP'})}
                      </TooltipText>
                    </Tooltip>
                  }
                  value={indate ? indate.split('T')[0] : ''}
                  onChange={(value) => {
                    onDiagnosisDataChange({indate: value})
                  }}
                  disabled={!isEditing}
                />
              </StyledSection>
              {/* END INPUT DATE */}

              <StyledSection>
                <Tooltip>
                  <StyledLabel>
                    <FaStethoscope />
                    {f({id: 'CLAIM.INPATIENT.DIAGNOSIS_MAIN'})}{' '}
                    <span style={{color: '#059BB6'}}>*</span>
                  </StyledLabel>
                  <TooltipText className='tooltip-text'>
                    {f({id: 'CLAIM.INPATIENT.DIAGNOSIS_MAIN_TOOLTIP'})}
                  </TooltipText>
                </Tooltip>
                <div style={{position: 'relative'}}>
                  <ErrorInput
                    type='text'
                    value={mainDiagnosis}
                    onChange={handleMainDiagnosisChange}
                    placeholder={f({id: 'CLAIM.INPATIENT.DIAGNOSIS_MAIN_PLACEHOLDER'})}
                    readOnly={!isEditing}
                    className={mainDiagnosis === '' && isEditing ? 'error' : ''}
                  />
                  {mainDiagnosis === '' && isEditing && (
                    <ValidationMessage $isError>
                      <FaTimes /> {f({id: 'CLAIM.INPATIENT.DIAGNOSIS_MAIN_REQUIRED'})}
                    </ValidationMessage>
                  )}
                  {isMainDiagnosisDropdownVisible && icdData.length > 0 && (
                    <div
                      ref={mainDiagnosisDropdownRef}
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        background: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        width: '100%',
                      }}
                    >
                      {icdData.map((icd) => (
                        <IcdDropdownItem
                          key={icd.id}
                          onClick={() => {
                            handleSelectIcd({code: icd.code, name: icd.name})
                            setIsMainDiagnosisDropdownVisible(false)
                          }}
                        >
                          <IcdName>{icd.name}</IcdName>
                          <IcdCode>{icd.code}</IcdCode>
                        </IcdDropdownItem>
                      ))}
                    </div>
                  )}
                </div>
              </StyledSection>

              <StyledSection>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px'}}>
                  <div>
                    <Tooltip>
                      <StyledLabel>
                        <FaStethoscope />
                        {f({id: 'CLAIM.INPATIENT.ICD10_KEY'}, {key: 2})}
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        {f({id: 'CLAIM.INPATIENT.ICD10_TOOLTIP'})}
                      </TooltipText>
                    </Tooltip>
                    <div style={{position: 'relative'}}>
                      <StyledInput
                        type='text'
                        value={supportingDiagnosis}
                        onChange={handleSupportingDiagnosisChange2}
                        placeholder={f({id: 'CLAIM.INPATIENT.ICD10_PLACEHOLDER'}, {key: 2})}
                        readOnly={!isEditing}
                      />
                      {isSupportingDiagnosis1DropdownVisible && icdData2.length > 0 && (
                        <div
                          ref={supportingDiagnosis1DropdownRef}
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            background: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            width: '100%',
                          }}
                        >
                          {icdData2.map((icd) => (
                            <IcdDropdownItem
                              key={icd.id}
                              onClick={() => {
                                handleSelectSupportingIcd({code: icd.code, name: icd.name})
                                setIsSupportingDiagnosis1DropdownVisible(false)
                              }}
                            >
                              <IcdName>{icd.name}</IcdName>
                              <IcdCode>{icd.code}</IcdCode>
                            </IcdDropdownItem>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Tooltip>
                      <StyledLabel>
                        <FaStethoscope />
                        {f({id: 'CLAIM.INPATIENT.ICD10_KEY'}, {key: 3})}
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        {f({id: 'CLAIM.INPATIENT.ICD10_TOOLTIP'})}
                      </TooltipText>
                    </Tooltip>
                    <div style={{position: 'relative'}}>
                      <StyledInput
                        type='text'
                        value={supportingDiagnosis3}
                        onChange={handleSupportingDiagnosisChange3}
                        placeholder={f({id: 'CLAIM.INPATIENT.ICD10_PLACEHOLDER'}, {key: 3})}
                        readOnly={!isEditing}
                      />
                      {isSupportingDiagnosis2DropdownVisible && icdData3.length > 0 && (
                        <div
                          ref={supportingDiagnosis2DropdownRef}
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            background: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            width: '100%',
                          }}
                        >
                          {icdData3.map((icd) => (
                            <IcdDropdownItem
                              key={icd.id}
                              onClick={() => {
                                handleSelectSupportingIcd3({code: icd.code, name: icd.name})
                                setIsSupportingDiagnosis2DropdownVisible(false)
                              }}
                            >
                              <IcdName>{icd.name}</IcdName>
                              <IcdCode>{icd.code}</IcdCode>
                            </IcdDropdownItem>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Tooltip>
                      <StyledLabel>
                        <FaStethoscope />
                        {f({id: 'CLAIM.INPATIENT.ICD10_KEY'}, {key: 4})}
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        {f({id: 'CLAIM.INPATIENT.ICD10_TOOLTIP'})}
                      </TooltipText>
                    </Tooltip>
                    <div style={{position: 'relative'}}>
                      <StyledInput
                        type='text'
                        value={supportingDiagnosis4}
                        onChange={handleSupportingDiagnosisChange4}
                        placeholder={f({id: 'CLAIM.INPATIENT.ICD10_PLACEHOLDER'}, {key: 4})}
                        readOnly={!isEditing}
                      />
                      {isSupportingDiagnosis3DropdownVisible && icdData4.length > 0 && (
                        <div
                          ref={supportingDiagnosis3DropdownRef}
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            background: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            width: '100%',
                          }}
                        >
                          {icdData4.map((icd) => (
                            <IcdDropdownItem
                              key={icd.id}
                              onClick={() => {
                                handleSelectSupportingIcd4({code: icd.code, name: icd.name})
                                setIsSupportingDiagnosis3DropdownVisible(false)
                              }}
                            >
                              <IcdName>{icd.name}</IcdName>
                              <IcdCode>{icd.code}</IcdCode>
                            </IcdDropdownItem>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </StyledSection>

              <StyledSection>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px'}}>
                  <div>
                    <Tooltip>
                      <StyledLabel>
                        <FaHospital />
                        {f({id: 'CLAIM.INPATIENT.DOCTOR_NAME'})}{' '}
                        <span style={{color: '#059BB6'}}>*</span>
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        {f({id: 'CLAIM.INPATIENT.DOCTOR_NAME_TOOLTIP'})}
                      </TooltipText>
                    </Tooltip>
                    <ErrorInput
                      type='text'
                      value={doctor}
                      onChange={(e) => onDiagnosisDataChange({doctor: e.target.value})}
                      readOnly={!isEditing}
                      className={doctor === '' && isEditing ? 'error' : ''}
                    />
                    {doctor === '' && isEditing && (
                      <ValidationMessage $isError>
                        <FaTimes /> {f({id: 'CLAIM.INPATIENT.DOCTOR_NAME_REQUIRED'})}
                      </ValidationMessage>
                    )}
                  </div>
                  <div>
                    <Tooltip>
                      <StyledLabel>
                        <FaHospital />
                        {f({id: 'CLAIM.INPATIENT.POLI_NAME'})}{' '}
                        <span style={{color: '#059BB6'}}>*</span>
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        {f({id: 'CLAIM.INPATIENT.POLI_NAME_TOOLTIP'})}
                      </TooltipText>
                    </Tooltip>
                    <ErrorInput
                      type='text'
                      value={polyName}
                      onChange={(e) => onDiagnosisDataChange({polyName: e.target.value})}
                      readOnly={!isEditing}
                      className={polyName === '' && isEditing ? 'error' : ''}
                    />
                    {polyName === '' && isEditing && (
                      <ValidationMessage $isError>
                        <FaTimes /> {f({id: 'CLAIM.INPATIENT.POLI_NAME_REQUIRED'})}
                      </ValidationMessage>
                    )}
                  </div>
                  <div>
                    <Tooltip>
                      <StyledLabel>
                        <FaHospital />
                        {f({id: 'CLAIM.INPATIENT.LOS_ESTIMATION'})}
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        {f({id: 'CLAIM.INPATIENT.LOS_ESTIMATION_TOOLTIP'})}
                      </TooltipText>
                    </Tooltip>
                    <div style={{position: 'relative', display: 'flex', alignItems: 'center'}}>
                      <StyledInput
                        type='text'
                        value={losEstimation || ''}
                        onChange={(e) => {
                          // Remove non-numeric characters and convert to string
                          const value = String(e.target.value).replace(/[^0-9]/g, '')
                          // Only update if empty or valid number between 0-999
                          if (value === '' || (Number(value) >= 0 && Number(value) <= 999)) {
                            onDiagnosisDataChange({losEstimation: value})
                          }
                        }}
                        onKeyPress={(e) => {
                          // Only allow numeric input
                          if (!/[0-9]/.test(e.key)) {
                            e.preventDefault()
                          }
                        }}
                        placeholder='0'
                        readOnly={!isEditing}
                        style={{
                          border: '1px solid #ced4da',
                          borderRadius: '4px',
                          height: '38px',
                          paddingRight: '60px',
                          width: '100%',
                          textAlign: 'right',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          right: '12px',
                          color: '#6c757d',
                          pointerEvents: 'none',
                          backgroundColor: 'transparent',
                          padding: '0 8px',
                          display: 'flex',
                          alignItems: 'center',
                          height: '100%',
                        }}
                      >
                        {f({id: 'CLAIM.INPATIENT.LOS_ESTIMATION_DAYS'})}
                      </div>
                    </div>
                  </div>
                </div>
              </StyledSection>

              <StyledSection>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
                  {/* Left column - Prescription - Enhanced UI */}
                  <div>
                    <Tooltip>
                      <StyledLabel>
                        <FaPrescription />
                        {f({id: 'CLAIM.INPATIENT.PRESCRIPTION'})}
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        {f({id: 'CLAIM.INPATIENT.PRESCRIPTION_TOOLTIP'})}
                      </TooltipText>
                    </Tooltip>
                    <UploadArea
                      $disabled={!isEditing}
                      style={{
                        background: isEditing
                          ? 'linear-gradient(145deg, #f8fbfc, #e9f5f8)'
                          : '#f5f5f5',
                        boxShadow: isEditing ? '0 4px 15px rgba(5, 155, 182, 0.08)' : 'none',
                        transition: 'all 0.3s ease',
                        padding: '12px 16px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '12px',
                        borderWidth: '2px',
                        borderStyle: 'dashed',
                        borderColor: isEditing ? '#059BB6' : '#ccc',
                        height: '40px',
                      }}
                    >
                      <input
                        type='file'
                        multiple
                        onChange={handleFileChange}
                        style={{display: 'none'}}
                        id='file-upload'
                        disabled={!isEditing}
                      />
                      <label
                        htmlFor='file-upload'
                        style={{
                          cursor: isEditing ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          width: '100%',
                          margin: 0,
                        }}
                      >
                        <div
                          style={{
                            background: isEditing ? 'rgba(5, 155, 182, 0.1)' : '#e9e9e9',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '12px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          <FaUpload
                            style={{
                              fontSize: '14px',
                              color: isEditing ? '#059BB6' : '#6c757d',
                            }}
                          />
                        </div>
                        <div>
                          <p
                            style={{
                              margin: 0,
                              color: isEditing ? '#2c3e50' : '#6c757d',
                              fontSize: '14px',
                              fontWeight: 500,
                            }}
                          >
                            {isEditing ? 'Upload Prescription Files' : 'File upload disabled'}
                          </p>
                        </div>
                      </label>
                    </UploadArea>

                    {isUploading && (
                      <div
                        style={{
                          marginTop: '16px',
                          background: 'white',
                          padding: '16px',
                          borderRadius: '10px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          border: '1px solid #e9ecef',
                        }}
                      >
                        <div
                          style={{
                            marginBottom: '10px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              color: '#059BB6',
                              fontWeight: 500,
                            }}
                          >
                            <Spinner
                              animation='border'
                              size='sm'
                              style={{
                                marginRight: '8px',
                                color: '#059BB6',
                                width: '16px',
                                height: '16px',
                              }}
                            />
                            {f({id: 'CLAIM.INPATIENT.UPLOADING_FILES'})}
                          </span>
                          <span
                            style={{
                              background: '#059BB6',
                              color: 'white',
                              borderRadius: '20px',
                              padding: '2px 10px',
                              fontSize: '14px',
                              fontWeight: 500,
                            }}
                          >
                            {uploadProgress}%
                          </span>
                        </div>
                        <div
                          style={{
                            background: '#f0f0f0',
                            borderRadius: '6px',
                            height: '8px',
                            overflow: 'hidden',
                          }}
                        >
                          <ProgressBar $progress={uploadProgress} />
                        </div>
                      </div>
                    )}

                    {documentsData.find((doc) => doc.label === 'Prescription')?.document?.length >
                      0 && (
                      <div
                        style={{
                          marginTop: '16px',
                          background: 'white',
                          borderRadius: '10px',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                          overflow: 'hidden',
                          border: '1px solid #e9ecef',
                          maxHeight: '250px',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <div
                          style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            background: '#f8f9fa',
                            flex: '0 0 auto',
                          }}
                        >
                          <FaPrescription style={{color: '#059BB6', marginRight: '8px'}} />
                          <span style={{fontWeight: 500, color: '#2c3e50'}}>
                            {f({id: 'CLAIM.INPATIENT.UPLOADED_PRESCRIPTION'})}
                          </span>
                        </div>
                        <div
                          style={{
                            padding: '8px',
                            overflowY: 'auto',
                            maxHeight: 'calc(250px - 45px)',
                            flex: '1 1 auto',
                          }}
                        >
                          {renderPrescriptionDocuments()}
                        </div>
                      </div>
                    )}

                    {successMessage && (
                      <div
                        style={{
                          marginTop: '16px',
                          padding: '12px 16px',
                          background: '#d4edda',
                          color: '#155724',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 500,
                          boxShadow: '0 2px 6px rgba(21, 87, 36, 0.1)',
                        }}
                      >
                        {successMessage}
                      </div>
                    )}
                  </div>

                  {/* Right column - Deposit */}
                  <div>
                    <Tooltip>
                      <StyledLabel>
                        <FaMoneyBillWave />
                        {f({id: 'CLAIM.INPATIENT.DEPOSIT'})}
                      </StyledLabel>
                      <TooltipText className='tooltip-text'>
                        {f({id: 'CLAIM.INPATIENT.DEPOSIT_TOOLTIP'})}
                      </TooltipText>
                    </Tooltip>
                    <div style={{position: 'relative', display: 'flex', alignItems: 'center'}}>
                      <div
                        style={{
                          position: 'absolute',
                          left: '0px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 1,
                          width: '70px',
                        }}
                      >
                        <Form.Select
                          value={selectedCurrency}
                          onChange={(e) => {
                            setSelectedCurrency(e.target.value)
                            onDiagnosisDataChange({currency: e.target.value})
                          }}
                          disabled={!isEditing}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            paddingLeft: '10px',
                            WebkitAppearance: isEditing ? 'menulist-button' : 'none',
                            appearance: isEditing ? 'menulist-button' : 'none',
                            fontSize: '14px',
                            width: '111px',
                            fontWeight: 500,
                            color: '#6c757d',
                          }}
                        >
                          <option value='IDR'>IDR</option>
                          <option value='USD'>USD</option>
                          <option value='SGD'>SGD</option>
                          <option value='MYR'>MYR</option>
                          <option value='THB'>THB</option>
                          <option value='YUAN'>YUAN</option>
                        </Form.Select>
                      </div>
                      <StyledInput
                        type='text'
                        value={deposit ? formatDisplayValue(deposit) : ''}
                        onChange={(e) => {
                          const input = e.target.value

                          // Step 1: Basic character validation - only allow digits, commas, and periods
                          if (!/^[\d.,]*$/.test(input)) {
                            return // Invalid character, reject input
                          }

                          // Step 2: Only allow one comma as decimal separator
                          const commaCount = (input.match(/,/g) || []).length
                          if (commaCount > 1) {
                            return // Multiple commas, reject input
                          }

                          // Step 3: Check for invalid patterns like ",....", ",,,...." or consecutive periods
                          if (
                            input.includes(',.') ||
                            input.includes(',,') ||
                            input.includes('..')
                          ) {
                            return // Invalid format with consecutive separators, reject input
                          }

                          // Step 4: Process and format the input
                          let formattedValue = input

                          // If there's a comma, split into integer and decimal parts
                          if (input.includes(',')) {
                            const [integerPart, decimalPart] = input.split(',')

                            // Format integer part (removing any existing dots first)
                            const cleanInteger = integerPart.replace(/\./g, '')

                            // Add thousand separators to integer part
                            const formattedInteger = cleanInteger.replace(
                              /\B(?=(\d{3})+(?!\d))/g,
                              '.'
                            )

                            // Recombine with the decimal part
                            formattedValue = `${formattedInteger},${decimalPart}`
                          } else {
                            // No comma, just format the whole number
                            const cleanValue = input.replace(/\./g, '')
                            formattedValue = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
                          }

                          onDiagnosisDataChange({deposit: formattedValue})
                        }}
                        readOnly={!isEditing}
                        style={{
                          paddingLeft: '112px',
                        }}
                      />
                    </div>
                  </div>
                </div>
              </StyledSection>

              <StyledSection>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
                  {/* Left column - Rencana Tindakan */}
                  <div>
                    <StyledLabel>
                      <FaNotesMedical />
                      {f({id: 'CLAIM.INPATIENT.TREATMENT_PLAN'})}{' '}
                      <span style={{color: '#059BB6'}}>*</span>
                    </StyledLabel>
                    <Form.Control
                      as='textarea'
                      rows={5}
                      value={rencanaTindakan || ''}
                      onChange={(e) => onDiagnosisDataChange({rencanaTindakan: e.target.value})}
                      readOnly={!isEditing}
                      isInvalid={!rencanaTindakan && isEditing}
                      style={{
                        ...(!isEditing && {
                          backgroundColor: '#f5f5f5',
                          color: '#6c757d',
                          cursor: 'not-allowed',
                        }),
                      }}
                    />
                    {!rencanaTindakan && isEditing && (
                      <ValidationMessage $isError>
                        <FaTimes /> {f({id: 'CLAIM.INPATIENT.TREATMENT_PLAN_REQUIRED'})}
                      </ValidationMessage>
                    )}
                  </div>

                  {/* Right column - Catatan */}
                  <div>
                    <StyledLabel>
                      <FaNotesMedical />
                      {f({id: 'CLAIM.INPATIENT.NOTES'})} <span style={{color: '#059BB6'}}>*</span>
                    </StyledLabel>
                    <Form.Control
                      as='textarea'
                      rows={5}
                      value={remark || ''}
                      onChange={(e) => onDiagnosisDataChange({remark: e.target.value})}
                      readOnly={!isEditing}
                      isInvalid={!remark && isEditing}
                      style={{
                        ...(!isEditing && {
                          backgroundColor: '#f5f5f5',
                          color: '#6c757d',
                          cursor: 'not-allowed',
                        }),
                      }}
                    />
                    {!remark && isEditing && (
                      <ValidationMessage $isError>
                        <FaTimes /> {f({id: 'CLAIM.INPATIENT.NOTES_REQUIRED'})}
                      </ValidationMessage>
                    )}
                  </div>
                </div>
              </StyledSection>

              <StyledSection>
                <StyledLabel>
                  <FaNotesMedical />
                  {f({id: 'CLAIM.INPATIENT.SPECIAL_TERMS'})}
                </StyledLabel>
                <div
                  dangerouslySetInnerHTML={{__html: parsedJson.Condition || ''}}
                  style={{
                    backgroundColor: '#f8f9fa',
                    color: '#6c757d',
                    cursor: 'not-allowed',
                    padding: '12px',
                    minHeight: '120px',
                    border: '2px solid #e9ecef',
                    borderRadius: '8px',
                    overflowY: 'auto',
                  }}
                />
              </StyledSection>

              {/* Section for remark reject void */}
              {(status === 'Void' || status === 'Reject') && (
                <StyledSection>
                  <StyledLabel>
                    <FaFileExcel /> Remark Reject Void
                  </StyledLabel>
                  <Form.Control
                    as='textarea'
                    rows={5}
                    value={claimDetails?.claimDetails?.claim?.remarkRejectVoid || remarkRejectVoid}
                    readOnly
                    style={{
                      backgroundColor: '#f8f9fa',
                      color: '#6c757d',
                      cursor: 'not-allowed',
                    }}
                    onChange={(e) => onDiagnosisDataChange({remarkRejectVoid: e.target.value})}
                  />
                </StyledSection>
              )}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  marginTop: '24px',
                }}
              >
                {status !== 'IGL RELEASED' &&
                  status !== 'Daily Monitoring' &&
                  status !== 'Void' &&
                  status !== 'Reject' &&
                  status !== 'Request FGL' &&
                  status !== 'FGL Review' &&
                  status !== 'Pending on FGL' &&
                  status !== 'FGL Released' &&
                  status !== 'FGL RELEASED' &&
                  status !== 'Final Document Complete' &&
                  status !== 'Initial Document Complete' &&
                  status !== 'initial document complete' &&
                  status !== 'IGL Released' &&
                  (isEditing ? (
                    <></>
                  ) : (
                    <StyledButton onClick={handleEditClick}>
                      <FaPencilAlt /> Edit
                    </StyledButton>
                  ))}
              </div>
            </>
          )}
        </form>
      </StyledCard>
    </>
  )
}

export default Diagnosis
